import { useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { flushSync } from 'react-dom';
import { elowenClient } from '../../lib/elowenClient';
import { fromHistory, keepOlderWindow, prependHistory, type ChatView } from '../../lib/transcript';

import { CHAT_HISTORY_PAGE_SIZE } from '../../lib/chatHistory';

/** A message is unseen only when its position is known and beyond the confirmed boundary. */
export function isUnseenPosition(branchDepth: number | undefined, readDepth: number | null | undefined): boolean {
  return readDepth !== undefined && branchDepth !== undefined && branchDepth > (readDepth ?? -1);
}

interface BrainChatHistoryOptions {
  getGeneration: () => number;
  getSession: () => string | undefined;
  /** The view of the last render. Used only to decide, outside any updater, whether a newest-window
   *  replacement keeps the older window (see `replaceNewestWindow`). */
  getView: () => ChatView;
  setView: Dispatch<SetStateAction<ChatView>>;
}

interface BrainChatHistory {
  hasMoreHistory: boolean;
  loadHistory: (generation: number, stillCurrent?: () => boolean) => Promise<boolean>;
  refreshSettlement: (generation: number, stillCurrent: () => boolean) => Promise<boolean>;
  loadOlder: (ready: () => Promise<void>) => Promise<void>;
  replaceNewestWindow: (next: (cur: ChatView) => ChatView, nextBefore: string | null, hasMore: boolean) => void;
  clearWindow: () => void;
}

export function useBrainChatHistory({ getGeneration, getSession, getView, setView }: BrainChatHistoryOptions): BrainChatHistory {
  // Lazy-load history state: `hasMoreHistory` is reactive (drives the older-history sentinel); the cursor and
  // the request in flight are refs — they change across async fetches and must not each trigger a re-render.
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const historyCursorRef = useRef<string | null>(null);
  const olderRequestRef = useRef<Promise<void> | null>(null);
  // Bumped by every newest-window replacement, window clear and newest-page read as it starts. A loadOlder or
  // loadHistory captures it and discards its result if it changed while the fetch was in flight — the connect
  // `generation` guard alone is not enough, because terminal journal repair, a native reconnect and a
  // `session` rebind keep the generation. A replacement that keeps the older window bumps it too: an older
  // page read before it may still show work the replacement has since settled, and the kept cursor simply
  // asks for that page again.
  const historyEpochRef = useRef(0);

  const resetWindow = (nextBefore: string | null, hasMore: boolean): void => {
    historyCursorRef.current = nextBefore;
    setHasMoreHistory(hasMore);
  };

  // Every newest-window replacement goes through here: a stream snapshot and a newest-page refetch. `next`
  // builds the replacement view from the current one. When the replacement's first stored turn is already on
  // screen behind older lazy-loaded pages, those pages stay together with their cursor (`keepOlderWindow`);
  // otherwise the window resets to the replacement's own cursor, as it always did.
  //
  // The decision reads the last rendered view, so it is made once, here, and the cursor follows it. Every
  // older page is rendered synchronously when it is applied (see `loadOlder`), so the rendered view already
  // holds every page the cursor has moved past.
  const replaceNewestWindow = (next: (cur: ChatView) => ChatView, nextBefore: string | null, hasMore: boolean): void => {
    historyEpochRef.current++;
    const rendered = getView();
    const probe = next(rendered);
    const keepOlder = keepOlderWindow(rendered, probe) !== probe;
    if (!keepOlder) resetWindow(nextBefore, hasMore);
    setView((cur) => (keepOlder ? keepOlderWindow(cur, next(cur)) : next(cur)));
  };

  // The newest page bootstraps the transcript; older pages lazy-load as the reader nears them. This serves
  // terminal truncated-journal repair and rewind, and keeps the lazy-load window only where
  // `replaceNewestWindow` proves it is still valid.
  //
  // Live `compacted` and `resync` frames reconnect for a fresh atomic snapshot instead: even a turn-boundary
  // frame can be followed by a new run before an independent history read returns. History includes only
  // the person's own message during a run; assistant/tool output stays provisional until it settles. The
  // snapshot carries history and the current run's journal together. `thinking` stays where the stream put it.
  const loadHistory = async (generation: number, stillCurrent?: () => boolean): Promise<boolean> => {
    const epoch = ++historyEpochRef.current;
    const session = getSession();
    const page = await elowenClient.brainMessagesPage(session, { limit: CHAT_HISTORY_PAGE_SIZE });
    if (generation !== getGeneration() || session !== getSession() || epoch !== historyEpochRef.current || (stillCurrent && !stillCurrent())) return false;
    const history = fromHistory(page.items);
    replaceNewestWindow((cur) => ({ ...history, thinking: cur.thinking, compacting: cur.compacting }), page.nextBefore, page.hasMore);
    return true;
  };

  // The terminal frame is not proof that a journal row settled. Read one bounded newest page and
  // apply only its source facts to matching displayed ids, leaving history, live text and cursors intact.
  const refreshSettlement = async (generation: number, stillCurrent: () => boolean): Promise<boolean> => {
    const session = getSession();
    if (!session) return false;
    const epoch = historyEpochRef.current;
    const page = await elowenClient.brainMessagesPage(session, { limit: CHAT_HISTORY_PAGE_SIZE });
    if (generation !== getGeneration() || session !== getSession() || epoch !== historyEpochRef.current || !stillCurrent()) return false;
    const settled = new Map(page.items.filter(row => row.settled && row.id && (row.role === 'user' || row.role === 'assistant'))
      .map(row => [row.id, { role: row.role, branchDepth: row.branchDepth }]));
    setView(current => {
      if (generation !== getGeneration() || session !== getSession() || epoch !== historyEpochRef.current || !stillCurrent()) return current;
      return { ...current, turns: current.turns.map(turn => {
        if ((turn.role !== 'you' && turn.role !== 'elowen') || !turn.id || (turn.settled && turn.branchDepth !== undefined)) return turn;
        const role = turn.role === 'you' ? 'user' : 'assistant';
        const source = settled.get(turn.id);
        return source?.role === role ? { ...turn, settled: true, ...(source.branchDepth === undefined ? {} : { branchDepth: source.branchDepth }) } : turn;
      }) };
    });
    return true;
  };

  // Fetch the next older page and prepend it. One request at a time: a caller that asks while one is in flight
  // (a second chat surface, or a surface remounted mid-fetch) gets that same request. A stale generation
  // (session switch) or epoch (a replacement or `session` rebind landed mid-fetch) discards the page instead of
  // tearing a hole in the replaced transcript. The page is rendered at once, so a replacement that follows
  // decides on a view that already holds it. `prependHistory` dedupes by id and leaves the live streaming
  // tail untouched, so a prepend mid-turn is safe.
  //
  // `ready` is the surface's commit gate: the fetched page is held until it resolves, so the surface can
  // keep the prepend out of a running touch gesture, whose momentum would override the position
  // correction the prepend needs. The fences are checked again after the wait: a conversation switch or a
  // newest-window replacement that landed meanwhile discards the held page.
  const loadOlder = (ready: () => Promise<void>): Promise<void> => {
    if (olderRequestRef.current) return olderRequestRef.current;
    const before = historyCursorRef.current;
    if (before === null) return Promise.resolve();
    const generation = getGeneration();
    const epoch = historyEpochRef.current;
    const superseded = (): boolean => generation !== getGeneration() || epoch !== historyEpochRef.current;
    const request = (async () => {
      try {
        const page = await elowenClient.brainMessagesPage(getSession(), { limit: CHAT_HISTORY_PAGE_SIZE, before });
        if (superseded()) return;
        await ready();
        if (superseded()) return;
        flushSync(() => {
          setView((cur) => prependHistory(cur, page.items));
          setHasMoreHistory(page.hasMore);
        });
        historyCursorRef.current = page.nextBefore;
      } finally {
        olderRequestRef.current = null;
      }
    })();
    olderRequestRef.current = request;
    return request;
  };

  const clearWindow = (): void => {
    historyEpochRef.current++;
    resetWindow(null, false);
  };

  return { hasMoreHistory, loadHistory, refreshSettlement, loadOlder, replaceNewestWindow, clearWindow };
}
